import { type PremiereToAirImportBatchComplete } from '@air/premiere-constants';

export type ImportBatchCompleteResult = PremiereToAirImportBatchComplete['data'];

type Waiter = {
  resolve: (result: ImportBatchCompleteResult) => void;
};

const waitersByBatchId = new Map<string, Waiter[]>();

/**
 * Resolves when the UXP host reports ImportBatchComplete for `batchId`.
 * {@link usePremiereImportLifecycle} calls {@link resolveImportBatchComplete}.
 */
export const waitForImportBatchComplete = (batchId: string, timeoutMs: number): Promise<ImportBatchCompleteResult> => {
  return new Promise((resolve) => {
    const queue = waitersByBatchId.get(batchId) ?? [];

    const waiter: Waiter = {
      resolve: (result) => {
        clearTimeout(timeoutId);
        resolve(result);
      },
    };

    queue.push(waiter);
    waitersByBatchId.set(batchId, queue);

    const timeoutId = setTimeout(() => {
      const currentQueue = waitersByBatchId.get(batchId);
      const index = currentQueue?.indexOf(waiter) ?? -1;

      if (index === -1 || currentQueue == null) {
        return;
      }

      const nextQueue = currentQueue.filter((entry) => entry !== waiter);

      if (nextQueue.length === 0) {
        waitersByBatchId.delete(batchId);
      } else {
        waitersByBatchId.set(batchId, nextQueue);
      }

      resolve({
        batchId,
        downloadFailures: [],
        importedFileNames: [],
        importError: 'Import timed out',
      });
    }, timeoutMs);
  });
};

export const resolveImportBatchComplete = (batchId: string, result: ImportBatchCompleteResult): void => {
  const queue = waitersByBatchId.get(batchId);

  if (!queue?.length) {
    return;
  }

  const [waiter, ...rest] = queue;

  if (rest.length === 0) {
    waitersByBatchId.delete(batchId);
  } else {
    waitersByBatchId.set(batchId, rest);
  }

  waiter.resolve(result);
};

/** Test-only reset — not used in production. */
export const resetImportBatchCompletionWaiters = (): void => {
  waitersByBatchId.clear();
};
